/**
 * Rate Limiting Service
 * Implements rate limiting and request throttling mechanisms
 */

export interface RateLimitConfig {
  windowMs: number; // Time window in milliseconds
  maxRequests: number; // Maximum requests per window
  skipSuccessfulRequests?: boolean;
  skipFailedRequests?: boolean;
  keyGenerator?: (clientId: string) => string;
}

export interface RateLimitResult {
  allowed: boolean;
  remaining: number;
  resetTime: number; // Seconds until reset
  totalRequests: number;
  windowStart: Date;
}

export interface RateLimitEntry {
  count: number;
  windowStart: Date;
  requests: RequestRecord[];
}

export interface RequestRecord {
  timestamp: Date;
  success: boolean;
  endpoint?: string;
}

export class RateLimitService {
  private rateLimits: Map<string, RateLimitEntry> = new Map();
  private config: RateLimitConfig;
  private cleanupInterval: NodeJS.Timeout;

  constructor(config: RateLimitConfig) {
    this.config = {
      skipSuccessfulRequests: false,
      skipFailedRequests: false,
      keyGenerator: (clientId: string) => clientId,
      ...config
    };

    // Start cleanup interval to remove expired entries
    this.cleanupInterval = setInterval(() => {
      this.cleanupExpiredEntries();
    }, this.config.windowMs);
  }

  /**
   * Check if request is within rate limits
   */
  async checkRateLimit(clientId: string, endpoint?: string): Promise<RateLimitResult> {
    const key = this.config.keyGenerator!(clientId);
    const now = new Date();
    
    // Get or create rate limit entry
    let entry = this.rateLimits.get(key);
    
    if (!entry || this.isWindowExpired(entry.windowStart, now)) {
      // Create new window
      entry = {
        count: 0,
        windowStart: now,
        requests: []
      };
      this.rateLimits.set(key, entry);
    }

    // Calculate remaining requests
    const remaining = Math.max(0, this.config.maxRequests - entry.count);
    const resetTime = Math.ceil((entry.windowStart.getTime() + this.config.windowMs - now.getTime()) / 1000);

    // Check if request is allowed
    const allowed = entry.count < this.config.maxRequests;

    if (allowed) {
      // Record the request
      entry.count++;
      entry.requests.push({
        timestamp: now,
        success: true, // Will be updated later if needed
        endpoint
      });
    }

    return {
      allowed,
      remaining: allowed ? remaining - 1 : remaining,
      resetTime: Math.max(0, resetTime),
      totalRequests: entry.count,
      windowStart: entry.windowStart
    };
  }

  /**
   * Record request result for rate limiting decisions
   */
  async recordRequestResult(clientId: string, success: boolean, _endpoint?: string): Promise<void> {
    const key = this.config.keyGenerator!(clientId);
    const entry = this.rateLimits.get(key);
    
    if (!entry) {
      return; // No entry found, nothing to update
    }

    // Find the most recent request and update its success status
    const recentRequest = entry.requests[entry.requests.length - 1];
    if (recentRequest) {
      recentRequest.success = success;
    }

    // Adjust count based on skip configuration
    if ((success && this.config.skipSuccessfulRequests) || 
        (!success && this.config.skipFailedRequests)) {
      entry.count = Math.max(0, entry.count - 1);
      entry.requests.pop(); // Remove the request from tracking
    }
  }

  /**
   * Get rate limit status for a client
   */
  async getRateLimitStatus(clientId: string): Promise<RateLimitResult | null> {
    const key = this.config.keyGenerator!(clientId);
    const entry = this.rateLimits.get(key);
    
    if (!entry) {
      return null;
    }

    const now = new Date();
    const remaining = Math.max(0, this.config.maxRequests - entry.count);
    const resetTime = Math.ceil((entry.windowStart.getTime() + this.config.windowMs - now.getTime()) / 1000);

    return {
      allowed: entry.count < this.config.maxRequests,
      remaining,
      resetTime: Math.max(0, resetTime),
      totalRequests: entry.count,
      windowStart: entry.windowStart
    };
  }

  /**
   * Reset rate limit for a specific client
   */
  async resetRateLimit(clientId: string): Promise<void> {
    const key = this.config.keyGenerator!(clientId);
    this.rateLimits.delete(key);
  }

  /**
   * Get all active rate limits
   */
  async getActiveRateLimits(): Promise<Map<string, RateLimitEntry>> {
    const now = new Date();
    const activeEntries = new Map<string, RateLimitEntry>();

    for (const [key, entry] of Array.from(this.rateLimits.entries())) {
      if (!this.isWindowExpired(entry.windowStart, now)) {
        activeEntries.set(key, { ...entry });
      }
    }

    return activeEntries;
  }

  /**
   * Get rate limiting statistics
   */
  async getRateLimitStats(): Promise<RateLimitStats> {
    const now = new Date();
    let totalClients = 0;
    let totalRequests = 0;
    let blockedRequests = 0;
    let activeWindows = 0;

    for (const [_key, entry] of Array.from(this.rateLimits.entries())) {
      if (!this.isWindowExpired(entry.windowStart, now)) {
        totalClients++;
        activeWindows++;
        totalRequests += entry.count;
        
        if (entry.count >= this.config.maxRequests) {
          blockedRequests += entry.count - this.config.maxRequests;
        }
      }
    }

    return {
      totalClients,
      totalRequests,
      blockedRequests,
      activeWindows,
      windowSizeMs: this.config.windowMs,
      maxRequestsPerWindow: this.config.maxRequests,
      blockRate: totalRequests > 0 ? blockedRequests / totalRequests : 0
    };
  }

  /**
   * Get rate limit hits (for monitoring)
   */
  async getRateLimitHits(): Promise<number> {
    const stats = await this.getRateLimitStats();
    return stats.blockedRequests;
  }

  /**
   * Implement sliding window rate limiting
   */
  async checkSlidingWindowRateLimit(clientId: string, endpoint?: string): Promise<RateLimitResult> {
    const key = this.config.keyGenerator!(clientId);
    const now = new Date();
    const windowStart = new Date(now.getTime() - this.config.windowMs);
    
    // Get or create entry
    let entry = this.rateLimits.get(key);
    if (!entry) {
      entry = {
        count: 0,
        windowStart: now,
        requests: []
      };
      this.rateLimits.set(key, entry);
    }

    // Remove requests outside the sliding window
    entry.requests = entry.requests.filter(req => req.timestamp >= windowStart);
    entry.count = entry.requests.length;

    // Check if request is allowed
    const allowed = entry.count < this.config.maxRequests;
    const remaining = Math.max(0, this.config.maxRequests - entry.count);

    if (allowed) {
      // Add new request
      entry.requests.push({
        timestamp: now,
        success: true,
        endpoint
      });
      entry.count++;
    }

    // Calculate reset time (time until oldest request expires)
    const oldestRequest = entry.requests[0];
    const resetTime = oldestRequest ? 
      Math.ceil((oldestRequest.timestamp.getTime() + this.config.windowMs - now.getTime()) / 1000) : 
      0;

    return {
      allowed,
      remaining: allowed ? remaining - 1 : remaining,
      resetTime: Math.max(0, resetTime),
      totalRequests: entry.count,
      windowStart: oldestRequest?.timestamp || now
    };
  }

  /**
   * Implement token bucket rate limiting
   */
  async checkTokenBucketRateLimit(clientId: string, tokens: number = 1): Promise<RateLimitResult> {
    const key = this.config.keyGenerator!(clientId);
    const now = new Date();
    
    // Get or create bucket
    let bucket = this.tokenBuckets.get(key);
    if (!bucket) {
      bucket = {
        tokens: this.config.maxRequests,
        lastRefill: now,
        capacity: this.config.maxRequests,
        refillRate: this.config.maxRequests / (this.config.windowMs / 1000) // tokens per second
      };
      this.tokenBuckets.set(key, bucket);
    }

    // Refill tokens based on time elapsed
    const timeElapsed = (now.getTime() - bucket.lastRefill.getTime()) / 1000;
    const tokensToAdd = Math.floor(timeElapsed * bucket.refillRate);
    
    if (tokensToAdd > 0) {
      bucket.tokens = Math.min(bucket.capacity, bucket.tokens + tokensToAdd);
      bucket.lastRefill = now;
    }

    // Check if enough tokens are available
    const allowed = bucket.tokens >= tokens;
    
    if (allowed) {
      bucket.tokens -= tokens;
    }

    // Calculate reset time
    const tokensNeeded = Math.max(0, tokens - bucket.tokens);
    const resetTime = tokensNeeded > 0 ? Math.ceil(tokensNeeded / bucket.refillRate) : 0;

    return {
      allowed,
      remaining: bucket.tokens,
      resetTime,
      totalRequests: bucket.capacity - bucket.tokens,
      windowStart: bucket.lastRefill
    };
  }

  private tokenBuckets: Map<string, TokenBucket> = new Map();

  /**
   * Check if rate limit window has expired
   */
  private isWindowExpired(windowStart: Date, now: Date): boolean {
    return now.getTime() - windowStart.getTime() >= this.config.windowMs;
  }

  /**
   * Clean up expired rate limit entries
   */
  private cleanupExpiredEntries(): void {
    const now = new Date();
    
    for (const [key, entry] of Array.from(this.rateLimits.entries())) {
      if (this.isWindowExpired(entry.windowStart, now)) {
        this.rateLimits.delete(key);
      }
    }

    // Also cleanup token buckets that haven't been used recently
    for (const [key, bucket] of Array.from(this.tokenBuckets.entries())) {
      if (now.getTime() - bucket.lastRefill.getTime() > this.config.windowMs * 2) {
        this.tokenBuckets.delete(key);
      }
    }
  }

  /**
   * Destroy the service and cleanup resources
   */
  destroy(): void {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
    }
    this.rateLimits.clear();
    this.tokenBuckets.clear();
  }
}

export interface RateLimitStats {
  totalClients: number;
  totalRequests: number;
  blockedRequests: number;
  activeWindows: number;
  windowSizeMs: number;
  maxRequestsPerWindow: number;
  blockRate: number;
}

interface TokenBucket {
  tokens: number;
  lastRefill: Date;
  capacity: number;
  refillRate: number; // tokens per second
}